/**
 * Helper text shown beneath each question in the
 * Telecom Pre-Investment Cyber Risk Triage questionnaire.
 *
 * Each entry is a short, business-readable sentence that explains
 * why the question matters. Rendered in QuestionCard when the
 * question_key is present in this map.
 */
export const TRIAGE_QUESTION_HELP: Record<string, string> = {
  // ---------- PROFILE ----------
  TPT_P1_COUNTRY:
    "The primary market shapes the regulatory regime, threat environment, and political exposure used to interpret later answers.",
  TPT_P2_SCALE:
    "Scale of operations is a proxy for blast-radius, regulatory attention, and the operational complexity of the target — applies to retail, infrastructure, and managed-service operators alike.",
  TPT_P3_CNI:
    "Critical National Infrastructure designation typically brings stricter regulatory expectations and elevated nation-state targeting.",
  TPT_P4_SUBSECTOR:
    "The telecom sub-sector determines whether the operator owns infrastructure or relies on third parties, which shifts the dependency and operational-risk profile.",
  TPT_P5_YEARS:
    "Operating tenure is a proxy for organisational maturity, technical-debt accumulation, and stability of the operating model.",
  TPT_P6_MA:
    "Recent M&A or restructuring frequently introduces integration gaps, identity-system fragmentation, and elevated transition risk.",
  TPT_P7_DIGITAL_REVENUE:
    "Higher digital-revenue dependence increases the financial impact of any service disruption or cyber incident.",

  // ---------- M1 — Business & Market Exposure ----------
  TPT_M1_01:
    "Highly visible operators attract more sophisticated threat actors and political attention.",
  TPT_M1_02:
    "Brand prominence influences reputational damage and media exposure following an incident.",
  TPT_M1_03:
    "Stable, transparent ownership reduces governance risk; opaque or shifting ownership often signals elevated transition and accountability risk.",
  TPT_M1_04:
    "Heavy dependence on a small number of customers concentrates revenue risk and amplifies the impact of disruption.",
  TPT_M1_05:
    "A near-term exit or transaction event materially elevates change-driven cyber and operational risk.",

  // ---------- M2 — Regulatory ----------
  TPT_M2_01:
    "More demanding regimes mean higher compliance burden and more aggressive incident-disclosure expectations.",
  TPT_M2_02:
    "Operating across multiple regulators increases conflict, complexity, and the cost of compliance failure.",

  // ---------- M3 — Infrastructure ----------
  TPT_M3_01:
    "A larger or more complex network footprint expands the inherent attack surface.",
  TPT_M3_02:
    "Core and interconnect assets are typically the highest-impact systems if compromised.",
  TPT_M3_03:
    "Edge and RAN exposure influences the operational complexity of detection and response.",
  TPT_M3_04:
    "Active technology transitions (e.g., legacy → cloud, 4G → 5G) introduce transient gaps and elevated implementation risk.",

  // ---------- M4 — Data ----------
  TPT_M4_01:
    "The volume and sensitivity of subscriber data drives privacy and regulatory exposure.",
  TPT_M4_02:
    "Cross-border data movement multiplies regulatory obligations and lawful-intercept complexity.",

  // ---------- M5 — Vendors ----------
  TPT_M5_01:
    "Concentration in a small number of suppliers magnifies third-party risk.",
  TPT_M5_02:
    "Exposure to high-risk or restricted vendors can trigger regulatory or geopolitical risk.",
  TPT_M5_03:
    "Heavy reliance on managed services shifts operational responsibility outside the operator's direct control.",

  // ---------- M6 — Operational complexity ----------
  TPT_M6_01:
    "High change volume and operational complexity elevate the chance of misconfiguration-driven incidents.",
  TPT_M6_02:
    "Shared or multi-tenant operating models increase blast-radius from a single failure.",
  TPT_M6_03:
    "A history of material cyber or operational incidents is a strong leading indicator of underlying control weakness.",
  TPT_M6_04:
    "Financial stress is consistently associated with deferred security investment and elevated insider risk.",

  // ---------- M7 — Privileged access ----------
  TPT_M7_01:
    "Breadth of privileged administrative access is typically the highest-impact compromise pathway.",
  TPT_M7_02:
    "Third-party administrative access expands the trust perimeter beyond the operator's controls.",

  // ---------- M8 — Revenue & service impact ----------
  TPT_M8_01:
    "Revenue dependence on digital channels translates directly into financial exposure during an outage.",
  TPT_M8_02:
    "Service-disruption sensitivity reflects how quickly customer harm and contractual penalties accumulate.",

  // ---------- M9 — External signals ----------
  TPT_M9_01:
    "Public disclosures and prior breaches are leading indicators of underlying programme maturity.",
  TPT_M9_02:
    "Geopolitical exposure can introduce nation-state targeting and regulatory turbulence.",

  // ---------- M10 — Recovery ----------
  TPT_M10_01:
    "The complexity of restoring critical services after a major disruption drives expected downtime and contractual exposure.",
};

/** Returns helper text for a given question_key, or empty string. */
export function getTriageQuestionHelp(questionKey?: string | null): string {
  if (!questionKey) return "";
  return TRIAGE_QUESTION_HELP[questionKey] ?? "";
}
